import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaFolderOpen, FaTimes, FaChevronRight } from 'react-icons/fa';
import config from '../config/config';
import { formatarData, formatarDocumento } from '../others/processos';
import { DetalheProcesso } from '../telas/Processos';

// Botão "Processos (n)" do cadastro do cliente ou do veículo: abre o histórico de processos dele, do mais recente
// para o mais antigo. O do veículo inclui os de antes do vínculo (pelo chassi); o do cliente, pelo CPF/CNPJ.
// Clicar num processo abre o detalhe aqui mesmo, por cima do cadastro (sem sair da página); ao fechar, volta
// para o histórico.
//
// GET /processos/veiculo/{id} | GET /processos/cliente/{id}
function BotaoProcessos({ tipo, id, titulo }) {
  const { API_BASE_URL } = config;
  const navigate = useNavigate();
  const [processos, setProcessos] = useState(null);
  const [aberto, setAberto] = useState(false);
  const [idDetalhe, setIdDetalhe] = useState(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    if (!id) return undefined;
    let cancelado = false;
    axios.get(`${API_BASE_URL}/processos/${tipo}/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } })
      .then(({ data }) => { if (!cancelado) setProcessos(data || []); })
      .catch(() => { if (!cancelado) setProcessos([]); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, id, recarga]);

  const quantidade = processos ? processos.length : null;

  return (
    <>
      <Botao type="button" onClick={() => setAberto(true)} title={`Processos ${tipo === 'veiculo' ? 'deste veículo' : 'deste cliente'}`}>
        <FaFolderOpen /> Processos{quantidade !== null ? ` (${quantidade})` : ''}
      </Botao>

      {aberto && !idDetalhe && (
        <Fundo onClick={() => setAberto(false)}>
          <Caixa onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <Cabecalho>
              <h3><FaFolderOpen /> Processos {titulo ? `· ${titulo}` : ''}</h3>
              <button type="button" onClick={() => setAberto(false)} aria-label="Fechar"><FaTimes /></button>
            </Cabecalho>
            <Corpo>
              {processos === null ? (
                <Vazio>Carregando...</Vazio>
              ) : processos.length === 0 ? (
                <Vazio>Nenhum processo registrado {tipo === 'veiculo' ? 'para este veículo' : 'para este cliente'}.</Vazio>
              ) : (
                <ListaHistorico>
                  {processos.map((p) => (
                    <Linha key={p.idProcesso} type="button" onClick={() => setIdDetalhe(p.idProcesso)}>
                      <Bolinha $enviado={p.status === 'ENVIADO'} title={p.status === 'ENVIADO' ? 'Com arquivos' : 'Pendente de upload'} />
                      <span className="data">{formatarData(p.dataProcesso)}</span>
                      <span className="info">
                        <strong>{tipo === 'veiculo' ? (p.nomeCliente || formatarDocumento(p.cpfCnpjCliente)) : (p.placa || p.chassi)}</strong>
                        <em>{p.servicos || 'Sem serviço informado'}{p.numeroOrdem ? ` · OS ${p.numeroOrdem}` : ''}</em>
                      </span>
                      <FaChevronRight className="seta" />
                    </Linha>
                  ))}
                </ListaHistorico>
              )}
              <Rodape>
                <button type="button" onClick={() => navigate('/processos')}>Ir para Processos</button>
              </Rodape>
            </Corpo>
          </Caixa>
        </Fundo>
      )}

      {idDetalhe && (
        <DetalheProcesso
          idProcesso={idDetalhe}
          onFechar={() => setIdDetalhe(null)}
          onAlterado={() => setRecarga((r) => r + 1)}
          onExcluido={() => { setIdDetalhe(null); setRecarga((r) => r + 1); }}
        />
      )}
    </>
  );
}

const Botao = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #eaf6f6;
  color: #006666;
  border: 1px solid rgba(0,128,128,0.3);
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  &:hover { background: #d9f0ef; }
`;

const Fundo = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1100;
`;

const Caixa = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  overflow: hidden;
`;

const Cabecalho = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #eef1f1;
  h3 { margin: 0; display: flex; align-items: center; gap: 8px; color: #004d4d; font-size: 1.05rem; }
  button { border: none; background: none; color: #95a5a6; cursor: pointer; font-size: 16px; }
`;

const Corpo = styled.div`
  padding: 14px 18px;
  overflow-y: auto;
`;

const ListaHistorico = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Linha = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  font: inherit;
  background: white;
  border: 1px solid #e3e8e8;
  border-radius: 8px;
  cursor: pointer;
  &:hover { border-color: #008080; }
  .data { color: #34495e; font-size: 12.5px; font-variant-numeric: tabular-nums; flex-shrink: 0; }
  .info { display: flex; flex-direction: column; flex: 1; min-width: 0; }
  .info strong { color: #004d4d; font-size: 13.5px; }
  .info em { color: #7f8c8d; font-size: 12px; font-style: normal; }
  .seta { color: #b0b8bc; flex-shrink: 0; }
`;

const Bolinha = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.$enviado ? '#27ae60' : '#e74c3c')};
`;

const Vazio = styled.p`
  text-align: center;
  color: #95a5a6;
  font-style: italic;
  margin: 18px 0;
`;

const Rodape = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  button { border: none; background: none; color: #008080; font-weight: 600; cursor: pointer; }
`;

export default BotaoProcessos;
